import { NetworkStatus, gql, useQuery } from '@apollo/client';
import IconClients from '@apollo/icons/default/IconClients.svg';
import IconHistory from '@apollo/icons/default/IconHistory.svg';
import IconTrash from '@apollo/icons/default/IconTrash.svg';
import {
  Badge,
  Button,
  ButtonGroup,
  Card,
  CardBody,
  CardHeader,
  CardHeaderControl,
  CardTitle,
  Checkbox,
  Code,
  Heading,
  Link,
  Table,
  TableContainer,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tooltip,
  Tr,
} from '@apollo/orbit';
import { Observatory } from '@apollo/space-kit/illustrations/Observatory';
import { Telescope } from '@apollo/space-kit/illustrations/Telescope';
import classnames from 'classnames';
import moment from 'moment';
import React, { useCallback, useMemo, useState } from 'react';
import { Redirect, useLocation } from 'react-router';
import { Link as ReactRouterLink } from 'react-router-dom';

import { useInternalGraphLinking } from 'src/app/graph/hooks/useInternalGraphLinking';
import {
  graphPersistedQueriesRouteConfig,
  persistedQueryListRouteConfig,
  variantPersistedQueriesRouteConfig,
} from 'src/app/graph/routes';
import { LazyUrlModal } from 'src/components/common/LazyUrlModal';
import { Loading } from 'src/components/common/loading/Loading';
import { NotFound } from 'src/components/common/notFound/NotFound';
import { PageTitle } from 'src/components/common/pageTitle/PageTitle';
import { ErrorPage } from 'src/components/errorPage/ErrorPage';
import { PermissionGuard } from 'src/components/permissionGuards/permissionGuard/PermissionGuard';
import { RelativeTimestamp } from 'src/components/timestamp/Timestamp';
import { useRouteParams } from 'src/hooks/useRouteParams';
import Config from 'src/lib/config';
import { getAbsoluteDate } from 'src/lib/date/date';
import { GraphQLTypes } from 'src/lib/graphqlTypes';
import { useLDFlag } from 'src/lib/launchDarkly';
import { RouteConfig } from 'src/lib/routeConfig/RouteConfig';
import { DocsRouter } from 'src/lib/routers';
import { setOverlayVisibleLocation } from 'src/lib/routing';

import { useBulkSelect } from '../../../../../hooks/useBulkSelect';
import { PersistedQueryListRepublishModal } from '../persistedQueriesHomePage/persistedQueryListActionMenu/persistedQueryListRepublishModal/PersistedQueryListRepublishModal';
import { PersistedQueryListConfigWizard } from '../persistedQueriesHomePage/persistedQueryListConfigWizard/PersistedQueryListConfigWizard';

import { PaginationControls, totalPageCount } from './PaginationControls';
import { PersistedQueryActionsMenu } from './persistedQueryActionMenu/PersistedQueryActionMenu';
import { PersistedQueryDetailsDrawer } from './persistedQueryDetailsDrawer/PersistedQueryDetailsDrawer';
import { PersistedQueryFilters } from './persistedQueryFilters/PersistedQueryFilters';
import { PersistedQueryListPageEmpty } from './PersistedQueryListPageEmpty';
import { PersistedQuerySortingControls } from './persistedQuerySortingControls/PersistedQuerySortingControls';
import { persistedQueryListQuery } from './queries';

const pqListPageSize = 100;

type BulkSelectItem = {
  id: string;
  clientName: string | null;
};

const PersistedQueryListPageContent = ({
  graphId,
  listId,
}: {
  graphId: string;
  listId?: string;
}) => {
  const { currentPersistedQueriesListRoute, isGraphPersistedQueriesRoute } =
    useInternalGraphLinking();
  const {
    graphVariant: graphVariantFromUrl,
    selectedOperation,
    clientName,
    search: nameSearchFromUrl,
    lastPublishedDate: lastPublishedDateFromUrl,
    lastPublishedOption: lastPublishedOptionFromUrl = 'now',
    lastPublishedType: lastPublishedTypeFromUrl = 'before',
    sortColumn: sortColumnFromUrl = GraphQLTypes.PersistedQuerySortColumn.NAME,
    sortDirection: sortDirectionFromUrl = GraphQLTypes.Ordering.ASCENDING,
    clientNames: clientNamesFromUrl = [],
    clientNamesGroupSelection: clientNamesGroupSelectionFromUrl = 'all',
  } = useRouteParams(currentPersistedQueriesListRoute);
  const [showPublishModal, setShowPublishModal] = useState(false);

  const lastPublishedDate = useMemo(
    () =>
      getAbsoluteDate(
        lastPublishedOptionFromUrl,
        lastPublishedDateFromUrl
          ? new Date(lastPublishedDateFromUrl)
          : undefined,
      ),
    [lastPublishedDateFromUrl, lastPublishedOptionFromUrl],
  );

  const [currentPage, setCurrentPage] = useState(0);
  const {
    data: newData,
    previousData,
    error,
    refetch,
    networkStatus,
  } = useQuery<
    GraphQLTypes.PersistedQueryListQuery,
    GraphQLTypes.PersistedQueryListQueryVariables
  >(persistedQueryListQuery, {
    variables: {
      graphId,
      listId: listId as string,
      first: pqListPageSize,
      filter: {
        name: nameSearchFromUrl,
        lastPublishedAt: {
          to:
            lastPublishedTypeFromUrl === 'before'
              ? moment(lastPublishedDate).toISOString()
              : undefined,
          from:
            lastPublishedTypeFromUrl === 'after'
              ? moment(lastPublishedDate).toISOString()
              : undefined,
        },
        clients:
          clientNamesFromUrl.length > 0
            ? // When name is '' we are looking for unidentified clients which are represented by null in the db
              clientNamesFromUrl.map((name) => (name === '' ? null : name))
            : clientNamesGroupSelectionFromUrl === 'none'
              ? []
              : undefined,
      },
      sort: {
        column: sortColumnFromUrl,
        direction: sortDirectionFromUrl,
      },
      clientNamesFirst: 1000,
    },
    skip: !listId,
    fetchPolicy: 'no-cache',
    notifyOnNetworkStatusChange: true,
  });
  const displayClientNames = useLDFlag(
    'persisted-queries-operation-client-name-display',
  );
  const shouldShowNewUI = useLDFlag('gov-pq-ux-sort-filter-delete');

  const { bulkSelectState, dispatchBulkSelectAction } =
    useBulkSelect<BulkSelectItem>();

  const data = newData ?? previousData;
  const persistedQueryList = data?.graph?.persistedQueryList;
  const operations =
    persistedQueryList?.operations.edges.map((e) => e.node) ?? [];
  const operationCount = persistedQueryList?.operations.totalCount ?? 0;

  const buildCount =
    persistedQueryList?.currentBuild.totalOperationsInList ?? 0;
  const hasOperations = buildCount > 0;
  const refetching = [
    NetworkStatus.setVariables,
    NetworkStatus.refetch,
  ].includes(networkStatus);

  const selectedOperationCount =
    bulkSelectState.state === 'bulk'
      ? operationCount - bulkSelectState.toggledItems.length
      : bulkSelectState.toggledItems.length;

  const location = useLocation();

  const linkedGraphVariant =
    graphVariantFromUrl ?? persistedQueryList?.linkedVariants[0]?.name;

  // Note: clientNames is limited to 1000 by default as to prevent abuse of publishing
  // custom manifest files with unreasonably large numbers of distinct clients. In the future
  // we may want to implement the full pagination logic, but for now this is good enough.
  const clientNames = useMemo(() => {
    return persistedQueryList?.clientNames.edges.map((e) => e.node) ?? [];
  }, [persistedQueryList?.clientNames]);

  const onChangeFilters = useCallback(() => {
    setCurrentPage(0);
    dispatchBulkSelectAction({ type: 'reset' });
  }, [dispatchBulkSelectAction]);

  if (!listId) {
    return (
      <>
        <PageTitle title="Persisted queries" />
        <div className="flex flex-1 flex-col items-center justify-center space-y-3 bg-primary">
          <Telescope className="w-40" />
          <div className="flex flex-col items-center">
            <Heading as="h3" size="xl">
              Let's start by creating a new persisted query list
            </Heading>
            <Text variant="body" className="max-w-[70ch] text-center">
              Use persisted queries to enforce a safelist on your variants or to
              manage a list of short query IDs that can be used in place of full
              freeform GraphQL in requests.{' '}
              <Link isExternal to={DocsRouter.path('PersistedQueries')}>
                Learn more
              </Link>
            </Text>
          </div>
          <PermissionGuard
            graphId={graphId}
            graphPermissions="canManagePersistedQueryLists"
            fallback={
              <Tooltip
                placement="bottom-end"
                label="Ask a graph administrator if you need to make a new list."
              >
                <Button isDisabled>Create new List</Button>
              </Tooltip>
            }
          >
            <Button
              as={ReactRouterLink}
              to={setOverlayVisibleLocation({
                location,
                overlayName: Config.modals.configurePersistedQueriesWizard,
              })}
            >
              Create new list
            </Button>
          </PermissionGuard>
        </div>
        <LazyUrlModal name={Config.modals.configurePersistedQueriesWizard}>
          {({ isOpen, closeModal }) => (
            <PersistedQueryListConfigWizard
              isOpen={isOpen}
              onClose={closeModal}
            />
          )}
        </LazyUrlModal>
      </>
    );
  }
  if (networkStatus === NetworkStatus.loading) {
    return <Loading />;
  }
  if (!persistedQueryList) {
    return <NotFound />;
  }
  if (error) {
    return <ErrorPage />;
  }
  if (shouldShowNewUI && hasOperations && !selectedOperation) {
    return (
      <Redirect
        to={currentPersistedQueriesListRoute.locationFrom({
          location,
          patch: {
            selectedOperation: operations[0]?.id,
            clientName: operations[0]?.clientName ?? undefined,
          },
        })}
      />
    );
  }

  return (
    <>
      {!hasOperations ? (
        <PersistedQueryListPageEmpty
          persistedQueryList={persistedQueryList}
          onPublishClick={() => setShowPublishModal(true)}
        />
      ) : shouldShowNewUI ? (
        <>
          <PageTitle
            title={persistedQueryList.name}
            rightSide={
              <ButtonGroup>
                {!isGraphPersistedQueriesRoute && (
                  <Button
                    variant="secondary"
                    as={ReactRouterLink}
                    to={graphPersistedQueriesRouteConfig.locationFrom({
                      location,
                      fromRouteConfig: variantPersistedQueriesRouteConfig,
                    })}
                  >
                    View all lists
                  </Button>
                )}

                <Button
                  onClick={() => setShowPublishModal(true)}
                  data-analytics-category="Persisted Queries"
                  data-analytics-label="list publish"
                >
                  Add operations
                </Button>
              </ButtonGroup>
            }
          />
          <div className="relative flex h-0 flex-1">
            <div className="flex flex-1 flex-col bg-primary">
              <div className="sticky top-0 z-10 flex flex-col border-b border-b-primary px-3">
                <PersistedQueryFilters
                  onChangeFilters={onChangeFilters}
                  clientNames={clientNames}
                />
                <div className="flex items-center justify-between">
                  <div className="flex items-center">
                    <PermissionGuard
                      graphId={graphId}
                      graphPermissions="canManagePersistedQueryLists"
                      fallback={null}
                    >
                      <Checkbox
                        className="-ml-3 p-3"
                        isIndeterminate={
                          bulkSelectState.toggledItems.length > 0
                        }
                        isChecked={bulkSelectState.state === 'bulk'}
                        onChange={() =>
                          dispatchBulkSelectAction({ type: 'toggle all' })
                        }
                      />
                    </PermissionGuard>
                    <div className="flex items-center space-x-2 py-2">
                      {bulkSelectState.state === 'bulk' ||
                      bulkSelectState.toggledItems.length > 0 ? (
                        <>
                          <Heading variant="title">Selected operations</Heading>
                          <Badge variant="neutralOutlined">
                            {selectedOperationCount}
                          </Badge>
                          <PermissionGuard
                            graphId={graphId}
                            graphPermissions="canManagePersistedQueryLists"
                            fallback={null}
                          >
                            <Button
                              variant="secondary"
                              size="xs"
                              leftIcon={<IconTrash />}
                            >
                              Delete
                            </Button>
                          </PermissionGuard>
                        </>
                      ) : (
                        <>
                          <Heading variant="title">All operations</Heading>
                          <Badge variant="neutralOutlined">
                            {operationCount}
                          </Badge>
                        </>
                      )}
                    </div>
                  </div>
                  <PersistedQuerySortingControls
                    size="xs"
                    variant="hidden"
                    className="max-lg:hidden"
                    sortColumn={sortColumnFromUrl}
                    sortDirection={sortDirectionFromUrl}
                  />
                </div>
              </div>
              <div
                className={classnames(
                  'flex-1 overflow-y-auto',
                  refetching && 'opacity-50',
                )}
              >
                {operationCount ? (
                  operations.map((op, i) => {
                    const isInToggledItems = bulkSelectState.toggledItems.some(
                      (item) =>
                        item.id === op.id && item.clientName === op.clientName,
                    );
                    return (
                      <div
                        key={JSON.stringify({ i: op.id, c: op.clientName })}
                        className={classnames(
                          'group flex items-start',
                          op.id === selectedOperation &&
                            op.clientName === (clientName ?? null)
                            ? 'border-l-4 border-l-info bg-info pl-2'
                            : 'pl-3 hover:bg-secondary',
                          i > 0 && 'border-t border-t-primary',
                        )}
                      >
                        <PermissionGuard
                          graphId={graphId}
                          graphPermissions="canManagePersistedQueryLists"
                          fallback={null}
                        >
                          <Checkbox
                            className={classnames(
                              'z-50 -ml-3 mt-1 p-3',
                              bulkSelectState.state === 'bulk' ||
                                bulkSelectState.toggledItems.length > 0
                                ? 'opacity-100'
                                : 'opacity-0 group-hover:opacity-100',
                            )}
                            isChecked={
                              (bulkSelectState.state === 'bulk' &&
                                !isInToggledItems) ||
                              (bulkSelectState.state === 'individual' &&
                                isInToggledItems)
                            }
                            onChange={() =>
                              dispatchBulkSelectAction({
                                type: 'toggle',
                                item: { id: op.id, clientName: op.clientName },
                              })
                            }
                          />
                        </PermissionGuard>
                        {/* To prevent collisions with interactive areas, the only area that is linkable is only around the text content. Perhaps there is a better way to handle this so we can have the whole item link and the checkbox do its own thing. */}
                        <Link
                          to={
                            op.clientName
                              ? currentPersistedQueriesListRoute.locationFrom({
                                  location,
                                  patch: {
                                    selectedOperation: op.id,
                                    clientName: op.clientName,
                                  },
                                })
                              : currentPersistedQueriesListRoute.locationFrom({
                                  location,
                                  patch: {
                                    selectedOperation: op.id,
                                    clientName: RouteConfig.DELETE,
                                  },
                                })
                          }
                          className="flex flex-1 flex-col space-y-1 py-3 no-underline hover:no-underline"
                        >
                          <p className="truncate">
                            <Text as="span" variant="bodySb" className="mr-2">
                              {op.name || (
                                <span className="italic">
                                  Unnamed operation
                                </span>
                              )}
                            </Text>

                            <Code
                              title={op.id}
                              className="max-w-[9ch] truncate"
                            >
                              {op.id}
                            </Code>
                          </p>
                          <p>
                            <Text as="span" variant="body">
                              <IconHistory className="mr-2 inline-block size-4 align-[-1px]" />
                              Last published{' '}
                            </Text>
                            <Text as="span" variant="bodySb">
                              <RelativeTimestamp value={op.lastPublishedAt} />
                            </Text>
                            {op.clientName && (
                              <>
                                <span className="mx-2 text-secondary">•</span>
                                <IconClients className="mr-2 inline-block size-4 align-[-1px]" />
                                <Text
                                  as="span"
                                  variant="body"
                                  className="italic"
                                >
                                  {op.clientName}
                                </Text>
                              </>
                            )}
                            {/* TODO(evan-pq): When we know how many versions of an operation there are we should show it here */}
                          </p>
                        </Link>
                      </div>
                    );
                  })
                ) : (
                  <div className="flex h-full flex-col items-center justify-center">
                    <Observatory className="mb-3" />
                    <Heading size="2xl" className="mb-2 text-center">
                      No operations found
                    </Heading>
                    <Text
                      variant="body"
                      className="mb-3 max-w-[65ch] text-center"
                    >
                      Try removing some filters or using different criteria.
                    </Text>
                    <Button
                      variant="primary"
                      as={ReactRouterLink}
                      to={currentPersistedQueriesListRoute.locationFrom({
                        location,
                        patch: {
                          lastPublishedDate: RouteConfig.DELETE,
                          lastPublishedOption: RouteConfig.DELETE,
                          lastPublishedType: RouteConfig.DELETE,
                          search: RouteConfig.DELETE,
                        },
                      })}
                    >
                      Reset filters
                    </Button>
                  </div>
                )}
              </div>
              <div className="flex h-20 items-center justify-between border-t border-t-primary bg-primary p-4">
                <PaginationControls
                  pageSize={pqListPageSize}
                  currentPage={currentPage}
                  totalCount={operationCount}
                  loading={refetching}
                  onGoToFirstPageClick={() => {
                    refetch({
                      first: pqListPageSize,
                      before: undefined,
                      last: undefined,
                      after: undefined,
                    }).catch(() => setCurrentPage(currentPage));
                    setCurrentPage(0);
                  }}
                  onGoToLastPageClick={() => {
                    refetch({
                      last: pqListPageSize,
                      before: undefined,
                      first: undefined,
                      after: undefined,
                    }).catch(() => setCurrentPage(currentPage));
                    setCurrentPage(
                      totalPageCount(pqListPageSize, operationCount) - 1,
                    );
                  }}
                  onPreviousPageClick={() => {
                    refetch({
                      before:
                        persistedQueryList?.operations.pageInfo.startCursor,
                      last: pqListPageSize,
                      first: undefined, // clear this when paginating back and forth
                      after: undefined, // clear this when paginating back and forth
                    }).catch(() => setCurrentPage(currentPage));
                    setCurrentPage(currentPage - 1);
                  }}
                  onNextPageClick={() => {
                    refetch({
                      after: persistedQueryList?.operations.pageInfo.endCursor,
                      first: pqListPageSize,
                      last: undefined, // clear this when paginating back and forth
                      before: undefined, // clear this when paginating back and forth
                    }).catch(() => setCurrentPage(currentPage));
                    setCurrentPage(currentPage + 1);
                  }}
                />
              </div>
            </div>
            <PersistedQueryDetailsDrawer
              listId={listId}
              linkedGraphVariant={linkedGraphVariant}
            />
          </div>
        </>
      ) : (
        <>
          <PageTitle title="Persisted Query List Details" />
          <section className="mx-8 mt-8 flex items-center">
            <Heading size="2xl" className="flex-1">
              {persistedQueryList?.name ?? '—'}
            </Heading>
            <Button
              onClick={() => setShowPublishModal(true)}
              data-analytics-category="Persisted Queries"
              data-analytics-label="list publish"
            >
              Add operations
            </Button>
          </section>
          <Card className="m-8">
            <CardHeader>
              <CardTitle>Operations</CardTitle>

              {/**
               * Note that we are only able to show the style "1 - 50 of 247" if we
               * maintain our own state in the UI that allows us to keep track of the
               * "current" page number.  We could imagine a world where the cursor could
               * also tell us which page we're on (or how many results are before / after this set).
               * In practice, this mostly works, but will break if e.g. sharing a URL with a cursor
               * that links to a page other than the first.
               */}
              <CardHeaderControl>
                <PaginationControls
                  pageSize={pqListPageSize}
                  currentPage={currentPage}
                  totalCount={operationCount}
                  loading={refetching}
                  onGoToFirstPageClick={() => {}}
                  onGoToLastPageClick={() => {}}
                  onPreviousPageClick={async () => {
                    try {
                      await refetch({
                        before:
                          persistedQueryList?.operations.pageInfo.startCursor,
                        last: pqListPageSize,
                        first: undefined, // clear this when paginating back and forth
                        after: undefined, // clear this when paginating back and forth
                      });
                      setCurrentPage(currentPage - 1);
                    } finally {
                      /* no-op on error as it's handled above */
                    }
                  }}
                  onNextPageClick={async () => {
                    try {
                      await refetch({
                        after:
                          persistedQueryList?.operations.pageInfo.endCursor,
                        first: pqListPageSize,
                        last: undefined, // clear this when paginating back and forth
                        before: undefined, // clear this when paginating back and forth
                      });
                      setCurrentPage(currentPage + 1);
                    } finally {
                      /* no-op on error as it's handled above */
                    }
                  }}
                />
              </CardHeaderControl>
            </CardHeader>
            <CardBody>
              <TableContainer>
                <Table
                  className={classnames('table-fixed', {
                    'opacity-50': refetching,
                  })}
                >
                  <Thead>
                    <Tr>
                      <Th className="w-80">Name</Th>
                      {/* Included to allow the first column to have a min width */}
                      <Th />
                      {displayClientNames && (
                        <Th className="w-[155px] whitespace-nowrap">Client</Th>
                      )}
                      <Th className="w-[155px] whitespace-nowrap">
                        First published
                      </Th>
                      <Th className="w-[155px] whitespace-nowrap">
                        Last published
                      </Th>
                      <Th isActionsMenu className="w-[110px]">
                        <span className="sr-only">Actions</span>
                      </Th>
                    </Tr>
                  </Thead>
                  <Tbody>
                    {operations.map((op) => {
                      return (
                        <Tr
                          key={JSON.stringify({ i: op.id, c: op.clientName })}
                        >
                          <Td colSpan={2}>
                            <p className="truncate">
                              <Text as="span" variant="bodySb" className="mr-2">
                                {op.name}
                              </Text>
                              <Code className="truncate">{op.id}</Code>
                            </p>

                            <Text
                              variant="code"
                              className="mt-2 truncate text-secondary"
                            >
                              {op.body}
                            </Text>
                          </Td>
                          {displayClientNames && (
                            <Td>
                              <Text>{op.clientName ?? '(any)'}</Text>
                            </Td>
                          )}
                          <Td>
                            <RelativeTimestamp value={op.firstPublishedAt} />
                          </Td>
                          <Td>
                            <RelativeTimestamp value={op.lastPublishedAt} />
                          </Td>
                          <Td isActionsMenu>
                            <PersistedQueryActionsMenu
                              graphId={graphId}
                              listId={listId}
                              persistedQueryId={op.id}
                              clientName={op.clientName}
                            />
                          </Td>
                        </Tr>
                      );
                    })}
                  </Tbody>
                </Table>
              </TableContainer>
            </CardBody>
          </Card>
        </>
      )}
      <PersistedQueryListRepublishModal
        isOpen={showPublishModal}
        graphId={graphId}
        listId={listId}
        onClose={() => setShowPublishModal(false)}
      />
    </>
  );
};

export const PersistedQueryListPage = () => {
  const { graphId, listId } = useRouteParams(persistedQueryListRouteConfig);
  return <PersistedQueryListPageContent graphId={graphId} listId={listId} />;
};

export const VariantPersistedQueryListPage = () => {
  const { graphId, graphVariant } = useRouteParams(
    variantPersistedQueriesRouteConfig,
  );

  const { data, loading } = useQuery<
    GraphQLTypes.VariantPersistedQueriesLayoutQuery,
    GraphQLTypes.VariantPersistedQueriesLayoutQueryVariables
  >(
    gql`
      query VariantPersistedQueriesLayoutQuery(
        $graphId: ID!
        $graphVariant: String!
      ) {
        graph(id: $graphId) {
          id
          variant(name: $graphVariant) {
            id
            persistedQueryList {
              id
            }
          }
        }
      }
    `,
    {
      variables: {
        graphId,
        graphVariant,
      },
    },
  );

  if (loading) {
    return <Loading />;
  }

  return (
    <PersistedQueryListPageContent
      graphId={graphId}
      listId={data?.graph?.variant?.persistedQueryList?.id}
    />
  );
};
